import React, { useState, useMemo } from 'react';
import { User, Phone, Mail, Star, Trash2, Edit2, Plus, X, Smartphone } from 'lucide-react';
import { useClinicContacts } from '../hooks/useClinicContacts';
import { useFormValidation } from '@hooks/useFormValidation';
import type { Contact, CreateContactRequest, UpdateContactRequest } from '../types/contact';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import PhoneLink from '@shared/components/common/PhoneLink';
import PhoneInput from '@shared/components/common/PhoneInput';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicContactsView.css';

interface ClinicContactsViewProps {
  clinicId: number;
  canEdit?: boolean;
}

interface ContactFormData {
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
  phoneExtension: string;
  mobilePhone: string;
  fax: string;
  preferredContactMethod: string;
  doNotContact: boolean;
  notes: string;
  contactRole: string;
  isPrimary: boolean;
}

const emptyFormData: ContactFormData = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  phoneExtension: '',
  mobilePhone: '',
  fax: '',
  preferredContactMethod: '',
  doNotContact: false,
  notes: '',
  contactRole: '',
  isPrimary: false,
};

const contactValidationRules = {
  firstName: (v: string) => (!v.trim() ? 'First name is required' : undefined),
  lastName: (v: string) => (!v.trim() ? 'Last name is required' : undefined),
  contactRole: (v: string) => (!v.trim() ? 'Contact role is required' : undefined),
  email: (v: string) =>
    v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? 'Invalid email format' : undefined,
};

const ClinicContactsView: React.FC<Readonly<ClinicContactsViewProps>> = ({
  clinicId,
  canEdit = true,
}) => {
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactFormData>(emptyFormData);

  // Delete confirmation
  const [deleteConfirmContact, setDeleteConfirmContact] = useState<Contact | null>(null);

  // Data hook
  const {
    contacts,
    isLoading,
    error,
    createContact,
    updateContact,
    deleteContact,
    isSaving,
    isDeleting,
  } = useClinicContacts(clinicId, {
    onCreateSuccess: () => closeModal(),
    onUpdateSuccess: () => closeModal(),
    onDeleteSuccess: () => setDeleteConfirmContact(null),
  });

  // Validation hook
  const { errors: formErrors, validate, clearErrors } = useFormValidation<ContactFormData>(
    contactValidationRules,
  );

  const openAddModal = () => {
    setEditingContact(null);
    setFormData(emptyFormData);
    clearErrors();
    setShowModal(true);
  };

  const openEditModal = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      title: contact.title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      phoneExtension: contact.phoneExtension || '',
      mobilePhone: contact.mobilePhone || '',
      fax: contact.fax || '',
      preferredContactMethod: contact.preferredContactMethod || '',
      doNotContact: contact.doNotContact || false,
      notes: contact.notes || '',
      contactRole: contact.contactRole,
      isPrimary: contact.isPrimary,
    });
    clearErrors();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
    setFormData(emptyFormData);
    clearErrors();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(formData)) return;

    const contactData: CreateContactRequest | UpdateContactRequest = {
      firstName: formData.firstName.trim() || undefined,
      lastName: formData.lastName.trim() || undefined,
      title: formData.title.trim() || undefined,
      email: formData.email.trim() || undefined,
      phone: formData.phone.trim() || undefined,
      phoneExtension: formData.phoneExtension.trim() || undefined,
      mobilePhone: formData.mobilePhone.trim() || undefined,
      fax: formData.fax.trim() || undefined,
      preferredContactMethod: formData.preferredContactMethod || undefined,
      doNotContact: formData.doNotContact,
      notes: formData.notes.trim() || undefined,
      contactRole: formData.contactRole,
      isPrimary: formData.isPrimary,
    };

    if (editingContact) {
      updateContact(editingContact.id, contactData);
    } else {
      createContact(contactData);
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteConfirmContact) return;
    deleteContact(deleteConfirmContact.id);
  };

  // Group contacts by role
  const contactsByRole = useMemo(() => {
    return contacts.reduce((acc, contact) => {
      const roleName = contact.contactRole;
      if (!acc[roleName]) {
        acc[roleName] = [];
      }
      acc[roleName].push(contact);
      return acc;
    }, {} as Record<string, Contact[]>);
  }, [contacts]);

  if (isLoading) {
    return (
      <div className="clinic-contacts-view">
        <div className="loading">Loading contacts...</div>
      </div>
    );
  }

  return (
    <div className="clinic-contacts-view">
      <div className="contacts-header">
        <div className="contacts-title">
          <h3>Contacts</h3>
          <span className="contacts-count">
            {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
          </span>
        </div>
        {canEdit && (
          <RequirePermission permission="UserCanEditClinicContacts">
            <button onClick={openAddModal} className="btn btn-primary btn-sm">
              <Plus size={16} />
              Add Contact
            </button>
          </RequirePermission>
        )}
      </div>

      {error && (
        <div className="contacts-error">
          {error.message || 'Failed to load contacts'}
        </div>
      )}

      {contacts.length === 0 ? (
        <div className="no-contacts">
          <User size={48} />
          <p>No contacts have been added for this clinic yet.</p>
          {canEdit && (
            <RequirePermission permission="UserCanEditClinicContacts">
              <button onClick={openAddModal} className="btn btn-primary">
                Add First Contact
              </button>
            </RequirePermission>
          )}
        </div>
      ) : (
        <div className="contacts-list">
          {Object.entries(contactsByRole).map(([roleName, roleContacts]) => (
            <div key={roleName} className="contact-role-group">
              <h4 className="role-group-title">{roleName}</h4>
              {roleContacts.map(contact => (
                <div key={contact.id} className="contact-card">
                  <div className="contact-main">
                    <div className="contact-avatar">
                      <User size={24} />
                    </div>
                    <div className="contact-info">
                      <div className="contact-name-row">
                        <span className="contact-name">
                          {contact.firstName} {contact.lastName}
                        </span>
                        {contact.isPrimary && (
                          <span className="primary-badge" title="Primary Contact">
                            <Star size={14} fill="currentColor" />
                            Primary
                          </span>
                        )}
                        {contact.doNotContact && (
                          <span className="dnc-badge" title="Do Not Contact">
                            DNC
                          </span>
                        )}
                        {contact.preferredContactMethod && (
                          <span className="preferred-method-badge" title="Preferred Contact Method">
                            {contact.preferredContactMethod}
                          </span>
                        )}
                      </div>
                      {contact.title && (
                        <span className="contact-title">{contact.title}</span>
                      )}
                      <div className="contact-details">
                        {contact.email && (
                          <span className="contact-detail">
                            <Mail size={14} />
                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                          </span>
                        )}
                        {contact.phone && (
                          <span className="contact-detail">
                            <Phone size={14} />
                            <PhoneLink phone={contact.phone} extension={contact.phoneExtension} />
                          </span>
                        )}
                        {contact.mobilePhone && (
                          <span className="contact-detail">
                            <Smartphone size={14} />
                            <PhoneLink phone={contact.mobilePhone} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="contact-actions">
                    {canEdit && (
                      <RequirePermission permission="UserCanEditClinicContacts">
                        <button
                          onClick={() => openEditModal(contact)}
                          className="action-btn"
                          title="Edit Contact"
                          aria-label="Edit Contact"
                          disabled={isSaving}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirmContact(contact)}
                          className="action-btn"
                          title="Delete Contact"
                          aria-label="Delete Contact"
                          disabled={isSaving}
                        >
                          <Trash2 size={16} />
                        </button>
                      </RequirePermission>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingContact ? 'Edit Contact' : 'Add Contact'}</h3>
              <button onClick={closeModal} className="modal-close" aria-label="Close">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-form-fields">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      id="firstName"
                      type="text"
                      value={formData.firstName}
                      onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                      className={formErrors.firstName ? 'error' : ''}
                    />
                    {formErrors.firstName && (
                      <span className="field-error">{formErrors.firstName}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      id="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                      className={formErrors.lastName ? 'error' : ''}
                    />
                    {formErrors.lastName && (
                      <span className="field-error">{formErrors.lastName}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <input
                      id="title"
                      type="text"
                      value={formData.title}
                      onChange={e => setFormData({ ...formData, title: e.target.value })}
                      placeholder="e.g., Office Manager"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="contactRole">Contact Role *</label>
                    <input
                      id="contactRole"
                      type="text"
                      value={formData.contactRole}
                      onChange={e => setFormData({ ...formData, contactRole: e.target.value })}
                      className={formErrors.contactRole ? 'error' : ''}
                      placeholder="e.g., Primary, Billing, Scheduling"
                    />
                    {formErrors.contactRole && (
                      <span className="field-error">{formErrors.contactRole}</span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className={formErrors.email ? 'error' : ''}
                    />
                    {formErrors.email && (
                      <span className="field-error">{formErrors.email}</span>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="preferredContactMethod">Preferred Contact Method</label>
                    <select
                      id="preferredContactMethod"
                      value={formData.preferredContactMethod}
                      onChange={e => setFormData({ ...formData, preferredContactMethod: e.target.value })}
                    >
                      <option value="">None specified</option>
                      <option value="Email">Email</option>
                      <option value="Phone">Phone</option>
                      <option value="Mobile">Mobile</option>
                      <option value="Fax">Fax</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone</label>
                    <PhoneInput
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={(value) => setFormData({ ...formData, phone: value })}
                      placeholder="(555) 555-5555"
                      showIcon={false}
                    />
                  </div>
                  <div className="form-group form-group-small">
                    <label htmlFor="phoneExtension">Ext.</label>
                    <input
                      id="phoneExtension"
                      type="text"
                      value={formData.phoneExtension}
                      onChange={e => setFormData({ ...formData, phoneExtension: e.target.value })}
                      placeholder="123"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mobilePhone">Mobile Phone</label>
                    <PhoneInput
                      id="mobilePhone"
                      name="mobilePhone"
                      value={formData.mobilePhone}
                      onChange={(value) => setFormData({ ...formData, mobilePhone: value })}
                      placeholder="(555) 555-5555"
                      showIcon={false}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fax">Fax</label>
                    <PhoneInput
                      id="fax"
                      name="fax"
                      value={formData.fax}
                      onChange={(value) => setFormData({ ...formData, fax: value })}
                      placeholder="(555) 555-5555"
                      showIcon={false}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="notes">Notes</label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={e => setFormData({ ...formData, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                </div>

                <div className="form-row checkbox-row">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isPrimary}
                      onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                    />
                    <span>Primary Contact for this role</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.doNotContact}
                      onChange={e => setFormData({ ...formData, doNotContact: e.target.checked })}
                    />
                    <span>Do Not Contact</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" onClick={closeModal} className="btn btn-secondary" disabled={isSaving}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving}>
                  {isSaving ? 'Saving...' : (editingContact ? 'Update Contact' : 'Add Contact')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteConfirmContact !== null}
        title="Delete Contact"
        message={deleteConfirmContact ? `Are you sure you want to delete ${deleteConfirmContact.firstName} ${deleteConfirmContact.lastName}? This action cannot be undone.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirmContact(null)}
        danger={true}
        loading={isDeleting}
      />
    </div>
  );
};

export default ClinicContactsView;
